/**
 * The Board's per-day relief-planning overlay (localStorage `oc-board-relief-<date>`), shared with the staff tabs.
 *
 *  - `reliefSup[scheduleId] = doctorId` — a relief supervisor who takes over the doctor role on that case.
 *  - `inRoom[scheduleId] = employeeId` — someone who takes over in the room (a doctor going in solo, or a CRNA).
 *  - `reliefSupFrom` / `inRoomFrom[scheduleId] = ISO time` — when that relief takes over, partway through the case
 *    (Braden 2026-09-14: "They can take over partway through and finish that case and then start the next one").
 *    Symphony records these for the handoffs it plans; a relief the runner drops in by hand has no time. An empty
 *    value marks where Symphony ended a relief's carry.
 *
 * A relief carries to the room's later cases (caseCoverage.carriedRelief); an empty relief entry ends the carry.
 */
export interface ReliefOverlay {
  inRoom: Record<string, string>;
  reliefSup: Record<string, string>;
  inRoomFrom?: Record<string, string>;
  reliefSupFrom?: Record<string, string>;
}

export interface ReliefHandoff { scheduleId: string; role: 'doctor' | 'crna'; employeeId: string; fromEmployeeId?: string; handoffUtc: string }

export const reliefOverlayKey = (date: string): string => `oc-board-relief-${date}`;

export const loadReliefOverlay = (date: string): ReliefOverlay => {
  try {
    const raw = localStorage.getItem(reliefOverlayKey(date));
    const parsed = raw ? JSON.parse(raw) : {};
    return { inRoom: parsed.inRoom || {}, reliefSup: parsed.reliefSup || {}, inRoomFrom: parsed.inRoomFrom || {}, reliefSupFrom: parsed.reliefSupFrom || {} };
  } catch {
    return { inRoom: {}, reliefSup: {}, inRoomFrom: {}, reliefSupFrom: {} };
  }
};

export const saveReliefOverlay = (date: string, overlay: ReliefOverlay): void => {
  try { localStorage.setItem(reliefOverlayKey(date), JSON.stringify(overlay)); } catch { /* ignore */ }
};

/** The same times map without these cases — a relief the runner clears or replaces no longer has Symphony's time. */
export const withoutHandoffTimes = (from: Record<string, string> | undefined, scheduleIds: string[]): Record<string, string> => {
  const next = { ...(from || {}) };
  for (const sid of scheduleIds) delete next[sid];
  return next;
};

/**
 * Write Symphony's planned mid-case handoffs into the overlay after a fresh allocation. The previous run's handoffs
 * (every entry Symphony timed or ended) are removed first; reliefs the runner placed by hand stay. Each handoff's relief
 * ends at the room's next case, which the reliever is allocated to directly.
 */
export const applyReliefHandoffs = (
  prev: ReliefOverlay,
  handoffs: ReliefHandoff[],
  roomCasesOf: (scheduleId: string) => { scheduleId: string; startTimeUtc: string }[],
): ReliefOverlay => {
  const next: ReliefOverlay = { inRoom: { ...prev.inRoom }, reliefSup: { ...prev.reliefSup }, inRoomFrom: {}, reliefSupFrom: {} };
  for (const sid of Object.keys(prev.inRoomFrom || {})) delete next.inRoom[sid];
  for (const sid of Object.keys(prev.reliefSupFrom || {})) delete next.reliefSup[sid];
  for (const h of handoffs) {
    const map = h.role === 'doctor' ? next.reliefSup : next.inRoom;
    const from = (h.role === 'doctor' ? next.reliefSupFrom : next.inRoomFrom)!;
    map[h.scheduleId] = h.employeeId;
    from[h.scheduleId] = h.handoffUtc;
    const cases = [...roomCasesOf(h.scheduleId)].sort((a, b) => Date.parse(a.startTimeUtc) - Date.parse(b.startTimeUtc));
    const at = cases.findIndex(c => c.scheduleId === h.scheduleId);
    const after = at >= 0 ? cases[at + 1] : undefined;
    if (after && !Object.prototype.hasOwnProperty.call(map, after.scheduleId)) { map[after.scheduleId] = ''; from[after.scheduleId] = ''; }
  }
  return next;
};

/** When `employeeId` takes over a case partway through, from the handoff times recorded in the overlay. */
export const handoffTimesFor = (overlay: ReliefOverlay, employeeId: string): { scheduleId: string; ms: number }[] => {
  const out: { scheduleId: string; ms: number }[] = [];
  const pairs: [Record<string, string>, Record<string, string> | undefined][] = [[overlay.reliefSup, overlay.reliefSupFrom], [overlay.inRoom, overlay.inRoomFrom]];
  for (const [map, from] of pairs) {
    for (const [sid, iso] of Object.entries(from || {})) {
      if (!iso || map[sid] !== employeeId) continue;
      const ms = Date.parse(iso);
      if (Number.isFinite(ms)) out.push({ scheduleId: sid, ms });
    }
  }
  return out;
};
